import { wakeApiBaseUrl } from 'infrastructure/api';
import { useAuthenticationProvider } from 'infrastructure/security/authentication/AuthenticationProvider';
import { WakeableEnvironment } from './constants';

export interface WakeResult {
  /** Number of ECS services restored. Zero means nothing had been recorded to restore. */
  services: number;
  /** When it goes back to sleep, or null for an unbounded wake. */
  keepAwakeUntil: Nullable<string>;
}

interface WakeResponseBody {
  services?: number;
  keep_awake_until?: Nullable<string>;
  message?: string;
}

/**
 * Posts the wake request to the API in the account being woken.
 *
 * Deliberately a plain fetch rather than the shared `apiService`. That wraps failures
 * in the application-wide error handler, which is right for the Davinci services it
 * was built for and wrong here: the only interesting failures are the scheduler's own
 * refusals, and their message is the thing worth showing. A generic toast saying
 * something went wrong would drop exactly the sentence the person needs.
 */
const useSleepConsoleApi = () => {
  const { getToken } = useAuthenticationProvider();

  const wake = async (environment: WakeableEnvironment, hours: number): Promise<WakeResult> => {
    const baseUrl = wakeApiBaseUrl(environment);

    if (!baseUrl) {
      throw new Error(`No wake endpoint is configured for ${environment} in this deployment.`);
    }

    let response: Response;

    try {
      response = await fetch(`${baseUrl}/wake/${environment}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'wake', hours }),
      });
    } catch (error) {
      throw new Error('Could not reach the wake endpoint. Check your connection and try again.');
    }

    if (response.status === 401 || response.status === 403) {
      throw new Error('Your session is no longer accepted. Reload the page to sign in again.');
    }

    // The scheduler answers refusals as JSON; a gateway-level failure may not, and
    // asking for JSON that is not there would replace a real status code with a
    // parse error.
    const body: WakeResponseBody = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(body.message || `The wake was refused (${response.status}).`);
    }

    return {
      services: body.services ?? 0,
      keepAwakeUntil: body.keep_awake_until ?? null,
    };
  };

  return { wake };
};

export default useSleepConsoleApi;
